import { type FC, type KeyboardEvent, useEffect } from 'react'
import { useRef, useState } from 'react'
import cx from 'classnames'
import { twMerge } from 'tailwind-merge'
import { type DefaultProps } from '../common'

export interface OtpInputProps extends DefaultProps {
  length?: number
  // eslint-disable-next-line no-unused-vars
  onComplete?: (pin: string) => void
  value?: string
  // eslint-disable-next-line no-unused-vars
  onChange?: (value: string) => void
  error?: string
}

export const OtpInput: FC<OtpInputProps> = ({
  // Common default properties for each component
  className,
  id,
  testId,
  ariaLabel,
  length = 6,
  onComplete,
  value,
  onChange,
  error,
}) => {
  // Component base class names. Can be overriden by className property.
  const baseClass = cx('')

  // Final class names
  const finalClasses = twMerge(baseClass, className)

  const [otp, setOtp] = useState<string[]>(value ? value.split('').slice(0, length) : new Array(length).fill(''))
  const otpBoxReference = useRef<(HTMLInputElement | null)[]>([])

  const handleChange = (val: string, index: number): void => {
    const newArr = [...otp]
    newArr[index] = val
    setOtp(newArr)

    if (val && index < length - 1) {
      otpBoxReference.current[index + 1]?.focus()
    }

    const otpString = newArr.join('')
    onChange?.(otpString)

    if (newArr.every((d) => d !== '')) {
      onComplete?.(otpString)
    }
  }

  // Update internal state when external value changes
  useEffect(() => {
    if (value !== undefined) {
      const newOtp = value.split('').slice(0, length)
      while (newOtp.length < length) {
        newOtp.push('')
      }
      setOtp(newOtp)
    }
  }, [value, length])

  const handleBackspaceAndEnter = (e: KeyboardEvent<HTMLInputElement>, index: number): void => {
    if (e.key === 'Backspace' && !e.currentTarget.value && index > 0) {
      otpBoxReference.current[index - 1]?.focus()
    }
    if (e.key === 'Enter' && e.currentTarget.value && index < length - 1) {
      otpBoxReference.current[index + 1]?.focus()
    }
  }

  return (
    <div
      className={finalClasses}
      {...(testId ? { 'data-testid': testId } : {})}
      {...(id ? { id } : {})}
      {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
      aria-hidden="true"
    >
      {/* Hidden input for iOS autofill */}
      <input
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        style={{
          position: 'absolute',
          left: '-9999px',
          opacity: 0,
          pointerEvents: 'none',
        }}
        onChange={(e) => {
          const autofillValue = e.target.value.slice(0, length)
          const newOtp = [...otp]

          // Fill digits from autofill
          autofillValue.split('').forEach((char, i) => {
            if (i < length) {
              newOtp[i] = char
            }
          })

          // Fill remaining with empty strings
          for (let i = autofillValue.length; i < length; i++) {
            newOtp[i] = ''
          }

          setOtp(newOtp)
          onChange?.(newOtp.join(''))

          // Focus the next empty field or complete
          if (autofillValue.length === length) {
            onComplete?.(autofillValue)
          } else if (autofillValue.length > 0) {
            otpBoxReference.current[autofillValue.length]?.focus()
          }
        }}
      />
      <div className="flex items-center gap-2">
        {otp.map((digit, index) => (
          <input
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={digit}
            maxLength={1}
            onChange={(e) => {
              handleChange(e.target.value, index)
            }}
            onKeyUp={(e) => {
              handleBackspaceAndEnter(e, index)
            }}
            onPaste={(e) => {
              e.preventDefault()
              const pastedData = e.clipboardData.getData('text').slice(0, length)
              const newOtp = [...otp]
              pastedData.split('').forEach((char, i) => {
                if (i < length) {
                  newOtp[i] = char
                }
              })
              setOtp(newOtp)
              onChange?.(newOtp.join(''))

              const lastFilledIndex = Math.min(pastedData.length, length - 1)
              otpBoxReference.current[lastFilledIndex]?.focus()

              // `digit` is already declared above - changing the param to `d` to avoid linting warnings
              if (newOtp.every((d) => d !== '')) {
                onComplete?.(newOtp.join(''))
              }
            }}
            ref={(reference) => {
              otpBoxReference.current[index] = reference
            }}
            className={cx(
              'typo-primary-2xl-regular border h-[64px] w-full max-w-[47px] p-3 rounded-md block focus:outline-2 focus:outline-gray-900 appearance-none w-full text-center',
              error ? 'border-text-error bg-red-50 focus:bg-white' : 'border-gray-200 focus:border-gray-900',
              digit ? 'bg-white border-gray-200' : '',
            )}
          />
        ))}
      </div>
      {Boolean(error) && <p className="text-text-error typo-primary-sm-semibold mt-3xs">{error}</p>}
    </div>
  )
}
